import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { navegacion } from '../ui/navegacion';

// Los filtros viven en la barra de direcciones.
//
// EL PROBLEMA. Hasta aquí el estado de cada pantalla existía solo en la
// memoria de React. Quien filtraba «productos bajo mínimo del proveedor
// ACME» y quería enseñárselo a alguien no tenía nada que mandar: dictaba
// los pasos por WhatsApp. Recargar la página lo perdía todo, y volver de
// la ficha de un producto devolvía a la lista sin filtrar, en la página 1.
//
// LA REGLA. El estado de una lista —qué se filtró y en qué página va— es
// parte de la dirección. Un enlace tiene que reconstruir exactamente lo
// que el otro está viendo.
//
// POR QUÉ replaceState Y NO pushState. Los filtros se escriben letra a
// letra: con `pushState`, teclear «ACME» dejaría cuatro entradas en el
// historial y el botón «Atrás» tendría que pulsarse cuatro veces para
// salir de la pantalla. Se sustituye la entrada actual. La contrapartida,
// asumida a conciencia: «Atrás» no deshace un filtro, sale del módulo.
//
// Y NUNCA SE BORRA EL STATE DE LA ENTRADA · TC-DR-AT (3 oct 2026). Estas
// escrituras pasaban `null` como state, y con eso se llevaban por delante
// lo que el módulo de navegación guarda en cada entrada del historial
// (qué pantalla es, su contexto, su scroll): el primer filtro tecleado
// dejaba «atrás» sin saber a dónde volver. Ahora escriben A TRAVÉS del
// módulo (`ui/navegacion.js`, la única puerta al historial), que conserva
// el state y además guarda ahí los filtros de la lista (`state.f`), para
// que volver de una ficha devuelva la lista como estaba.

export const CLAVE_MODULO = 'm';

// La escritura va con un respiro. No es rendimiento: Safari limita las
// llamadas a replaceState (~100 en 30 s) y a partir de ahí las descarta
// con un aviso en consola. Teclear rápido en un buscador llega a ese tope.
const RETARDO_ESCRITURA = 300;

// --- Serialización -----------------------------------------------------
// El tipo lo dicta el valor POR DEFECTO, que es el único sitio donde ya
// está declarado. Así `?solo_activos=0` vuelve como booleano false y no
// como la cadena "0", que es verdadera y arruinaría el filtro en silencio.
function aTexto(valor) {
  if (typeof valor === 'boolean') return valor ? '1' : '0';
  return String(valor);
}

function desdeTexto(texto, porDefecto) {
  if (typeof porDefecto === 'boolean') return texto === '1' || texto === 'true';
  if (typeof porDefecto === 'number') {
    const n = Number(texto);
    return Number.isFinite(n) ? n : porDefecto;
  }
  return texto;
}

function leerUrl(base) {
  const q = new URLSearchParams(window.location.search);
  const salida = {};
  for (const [clave, porDefecto] of Object.entries(base)) {
    salida[clave] = q.has(clave) ? desdeTexto(q.get(clave), porDefecto) : porDefecto;
  }
  return salida;
}

function escribirUrl(base, valores, entrada) {
  // Si la entrada vigente ya es otra (se cambió de pantalla), no se
  // escribe: un temporizador de 300 ms no puede pisar la dirección de la
  // pantalla siguiente.
  if (entrada !== undefined && navegacion.idEntrada() !== entrada) return;
  const q = new URLSearchParams(window.location.search);
  // EL FILTRO QUE TRAJO EL ENLACE SE QUEDA EN LA DIRECCIÓN · auditoría de
  // TC-DR-AT, H3 (4 oct 2026). Desde que el contexto del módulo se lee de
  // la dirección, `/app?m=existencias&ubicacion=A-2` entrega `A-2` a la
  // pantalla como contexto, la pantalla lo declara como su valor POR
  // DEFECTO, y la regla de abajo («lo normal no se escribe») lo quitaba de
  // la dirección: el filtro se aplicaba y el enlace copiado de la barra
  // abría la lista sin filtrar. Un valor que coincide con el contexto de
  // la entrada no es «lo normal»: es lo que el enlace pidió, y se conserva.
  const ctx = navegacion.pantalla()?.ctx;
  for (const [clave, porDefecto] of Object.entries(base)) {
    const valor = valores[clave];
    // Solo se escribe lo que se apartó de lo normal. Una dirección con
    // catorce parámetros en su valor por defecto no se puede leer de un
    // vistazo ni pegar en un mensaje sin que dé pereza.
    const vacio = valor === '' || valor === null || valor === undefined;
    const delContexto = !vacio && ctx && typeof ctx === 'object'
      && Object.prototype.hasOwnProperty.call(ctx, clave) && aTexto(ctx[clave]) === aTexto(valor);
    if (vacio || (aTexto(valor) === aTexto(porDefecto) && !delContexto)) q.delete(clave);
    else q.set(clave, aTexto(valor));
  }
  const cadena = q.toString();
  const previos = navegacion.estadoDeEntrada()?.f;
  navegacion.reescribirUrl(
    cadena ? `${window.location.pathname}?${cadena}` : window.location.pathname,
    { filtros: { ...(previos || {}), ...valores } },
  );
}

// Lo que la entrada del historial recuerda de esta lista manda sobre la
// dirección: así se conserva también un filtro vaciado a mano cuya clave
// coincide con una del contexto del módulo (`sku`, `ubicacion`).
function leerEntrada(base) {
  const salida = leerUrl(base);
  const f = navegacion.estadoDeEntrada()?.f;
  if (f && typeof f === 'object') {
    for (const clave of Object.keys(base)) {
      if (Object.prototype.hasOwnProperty.call(f, clave)) salida[clave] = f[clave];
    }
  }
  return salida;
}

// --- Módulo activo -----------------------------------------------------
// Qué pantalla está abierta también es parte de la dirección: sin esto,
// un enlace con filtros llegaría a la pantalla de inicio del destinatario.
export function leerModuloUrl() {
  return new URLSearchParams(window.location.search).get(CLAVE_MODULO);
}

export function fijarModuloUrl(id) {
  // Cambiar de módulo LIMPIA los parámetros del anterior. Arrastrar
  // `bajo_minimo=1` de Productos hasta Auditoría no filtraría nada, pero
  // dejaría la dirección mintiendo sobre lo que se está viendo.
  const q = new URLSearchParams();
  if (id) q.set(CLAVE_MODULO, id);
  const cadena = q.toString();
  navegacion.reescribirUrl(
    cadena ? `${window.location.pathname}?${cadena}` : window.location.pathname,
  );
}

// --- Estado sincronizado ----------------------------------------------
// `iniciales` se congela en el primer render: es la declaración de qué
// claves y de qué tipo maneja esta pantalla, no un valor que cambie.
export function useEstadoUrl(iniciales) {
  // Congelado con estado y no con una referencia: una referencia no se
  // puede leer durante el render —React lo prohíbe y el linter lo cazó—,
  // y aquí hace falta justo en el render inicial. Un estado sin setter es
  // exactamente «un valor que se calcula una vez y ya no cambia».
  const [base] = useState(iniciales);
  const [valores, setValores] = useState(() => leerEntrada(base));
  // La entrada del historial en la que nació esta lista: solo ahí escribe.
  const [entrada] = useState(() => navegacion.idEntrada());

  useEffect(() => {
    const t = setTimeout(() => escribirUrl(base, valores, entrada), RETARDO_ESCRITURA);
    return () => clearTimeout(t);
  }, [base, valores, entrada]);

  // Quien teclea un filtro y abre una ficha antes de los 300 ms no lo
  // pierde: justo antes de dejar la pantalla se vuelca lo vigente.
  const vigentes = useRef(valores);
  useEffect(() => { vigentes.current = valores; });
  useEffect(
    () => navegacion.antesDeSalir(() => escribirUrl(base, vigentes.current, entrada)),
    [base, entrada],
  );

  return [valores, setValores];
}

// --- Lo que usan las listas -------------------------------------------
// Filtros + página, con la regla que más se olvida escrita una sola vez:
// TOCAR UN FILTRO VUELVE A LA PÁGINA 1.
//
// Sin esto, quien está en la página 4 y escribe en el buscador se queda
// mirando una tabla vacía —hay tres resultados y sigue pidiendo del 300
// en adelante— y concluye, con razón, que la búsqueda no funciona. Antes
// cada pantalla se acordaba por su cuenta, repitiendo `setPagina(0)` en
// cada uno de sus filtros; bastaba olvidarlo en uno.
export function useListaUrl(iniciales, { porPagina = 100 } = {}) {
  const [estado, setEstado] = useEstadoUrl({ ...iniciales, pagina: 0 });

  const filtros = useMemo(() => {
    const copia = { ...estado };
    delete copia.pagina;
    return copia;
  }, [estado]);

  const fijarFiltro = useCallback((parcial) => {
    setEstado((e) => ({ ...e, ...parcial, pagina: 0 }));
  }, [setEstado]);

  const fijarPagina = useCallback((p) => {
    setEstado((e) => ({ ...e, pagina: Math.max(0, p) }));
  }, [setEstado]);

  return {
    filtros,
    pagina: estado.pagina,
    porPagina,
    desplazamiento: estado.pagina * porPagina,
    fijarFiltro,
    fijarPagina,
  };
}

// Arma la cadena de consulta de una lista: filtros con valor, más límite y
// desplazamiento. Los booleanos van como `true`/`false` porque es lo que
// entiende FastAPI, no como el `1`/`0` compacto de la barra de direcciones.
export function consultaLista(filtros, { limite, desplazamiento, omitir = [] } = {}) {
  const q = new URLSearchParams();
  for (const [clave, valor] of Object.entries(filtros)) {
    if (omitir.includes(clave)) continue;
    if (valor === '' || valor === null || valor === undefined) continue;
    q.set(clave, typeof valor === 'boolean' ? String(valor) : valor);
  }
  if (limite !== undefined) q.set('limite', limite);
  if (desplazamiento) q.set('desplazamiento', desplazamiento);
  return q.toString();
}
